import { useCallback, useEffect, useMemo, useState } from "react";
import { usePermissionCheck } from "../hooks/usePermissionCheck";
import { getUsersPaginated } from "../api/adminUsersApi";
import { getPendingContentParts, getPendingContentSubmissions } from "../api/adminContentApi";
import { getCreatorPayouts, getOutstandingCreatorPayouts } from "../api/adminPayoutsApi";
import { exportClientEventsCsv, getAdminLogSummary, getClientEventSummary, getCreatorAccessViolations } from "../api/adminStatsApi";
import { notifyDialog } from "../utils/dialogs";

type OpsSnapshot = {
  users: {
    totalUsers: number;
    sampleSize: number;
    bannedAllCount: number;
    commentBannedCount: number;
    creatorCount: number;
    staffCount: number;
    excludedFromViewMetricsCount: number;
  };
  content: {
    pendingSubmissions: number;
    pendingParts: number;
  };
  finance: {
    outstandingCreators: number;
    outstandingShareToken: number;
    outstandingCurrencyAmount: number;
    openPayoutRecords: number;
    paidPayoutRecords: number;
  };
  security: {
    logErrors: number;
    creatorAccessViolations: number;
  };
  analytics: {
    totalEvents: number;
    sinceUtc: string | null;
    selectedHours: number;
    selectedAction: string;
    adsenseAttempts: number;
    adsenseSuccessRate: number;
    topVariant: string;
    topSlot: string;
    variantBreakdown: Array<{
      variant: string;
      attempts: number;
      success: number;
      failed: number;
      successRate: number;
    }>;
    hourly: Array<{
      hourUtc: string;
      total: number;
      attempts: number;
      success: number;
      failed: number;
    }>;
  };
};

const EMPTY_SNAPSHOT: OpsSnapshot = {
  users: {
    totalUsers: 0,
    sampleSize: 0,
    bannedAllCount: 0,
    commentBannedCount: 0,
    creatorCount: 0,
    staffCount: 0,
    excludedFromViewMetricsCount: 0,
  },
  content: {
    pendingSubmissions: 0,
    pendingParts: 0,
  },
  finance: {
    outstandingCreators: 0,
    outstandingShareToken: 0,
    outstandingCurrencyAmount: 0,
    openPayoutRecords: 0,
    paidPayoutRecords: 0,
  },
  security: {
    logErrors: 0,
    creatorAccessViolations: 0,
  },
  analytics: {
    totalEvents: 0,
    sinceUtc: null,
    selectedHours: 24,
    selectedAction: "all",
    adsenseAttempts: 0,
    adsenseSuccessRate: 0,
    topVariant: "-",
    topSlot: "-",
    variantBreakdown: [],
    hourly: [],
  },
};

function isCreatorRole(role: string | null | undefined): boolean {
  if (!role) {
    return false;
  }
  return role.trim().toLowerCase() === "creator";
}

function isStaffRole(role: string | null | undefined): boolean {
  if (!role) {
    return false;
  }
  const normalized = role.trim().toLowerCase();
  return normalized.includes("admin") || normalized.includes("mod");
}

export default function AdminOpsCenter() {
  const { loading: permissionLoading, allowed } = usePermissionCheck("Dashboard.View");
  const [loading, setLoading] = useState(true);
  const [exportingAnalytics, setExportingAnalytics] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [snapshot, setSnapshot] = useState<OpsSnapshot>(EMPTY_SNAPSHOT);
  const [analyticsHours, setAnalyticsHours] = useState(24);
  const [analyticsAction, setAnalyticsAction] = useState("all");

  const loadSnapshot = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [users, submissions, parts, outstanding, payouts, logSummary, violations, clientEventSummary] = await Promise.all([
        getUsersPaginated({ take: 200, skip: 0, sort: "nameAsc" }),
        getPendingContentSubmissions(0, 1),
        getPendingContentParts(0, 1),
        getOutstandingCreatorPayouts(),
        getCreatorPayouts(),
        getAdminLogSummary("24h"),
        getCreatorAccessViolations("24h"),
        getClientEventSummary(analyticsHours, analyticsAction === "all" ? undefined : analyticsAction),
      ]);

      const sampleUsers = users.items ?? [];
      const sampleRoles = sampleUsers.map((user) => {
        if (Array.isArray(user.roles) && user.roles.length > 0) {
          return user.roles;
        }
        return [user.role];
      });

      const bannedAllCount = sampleUsers.filter((user) => user.isBannedAll).length;
      const commentBannedCount = sampleUsers.filter((user) => user.isCommentBanned).length;
      const excludedFromViewMetricsCount = sampleUsers.filter((user) => user.excludeFromViewMetrics).length;
      const creatorCount = sampleRoles.filter((roles) => roles.some((role) => isCreatorRole(role))).length;
      const staffCount = sampleRoles.filter((roles) => roles.some((role) => isStaffRole(role))).length;

      const outstandingShareToken = outstanding.reduce((sum, row) => sum + Number(row.shareTokenAmount ?? 0), 0);
      const outstandingCurrencyAmount = outstanding.reduce((sum, row) => sum + Number(row.currencyAmount ?? 0), 0);
      const openPayoutRecords = payouts.filter((row) => String(row.status ?? "").toLowerCase() !== "paid").length;
      const paidPayoutRecords = payouts.length - openPayoutRecords;

      const topVariant = (clientEventSummary?.variants?.[0]?.variant ?? "-").toString();
      const topSlot = (clientEventSummary?.topSlots?.[0]?.slot ?? "-").toString();

      setSnapshot({
        users: {
          totalUsers: users.totalCount ?? 0,
          sampleSize: sampleUsers.length,
          bannedAllCount,
          commentBannedCount,
          creatorCount,
          staffCount,
          excludedFromViewMetricsCount,
        },
        content: {
          pendingSubmissions: submissions.totalCount ?? 0,
          pendingParts: parts.totalCount ?? 0,
        },
        finance: {
          outstandingCreators: outstanding.length,
          outstandingShareToken,
          outstandingCurrencyAmount,
          openPayoutRecords,
          paidPayoutRecords,
        },
        security: {
          logErrors: Number(logSummary?.errorCount ?? 0),
          creatorAccessViolations: Number(violations?.total ?? 0),
        },
        analytics: {
          totalEvents: Number(clientEventSummary?.total ?? 0),
          sinceUtc: clientEventSummary?.sinceUtc ?? null,
          selectedHours: analyticsHours,
          selectedAction: analyticsAction,
          adsenseAttempts: Number(clientEventSummary?.adsense?.attempts ?? 0),
          adsenseSuccessRate: Number(clientEventSummary?.adsense?.successRate ?? 0),
          topVariant,
          topSlot,
          variantBreakdown: clientEventSummary?.variantBreakdown ?? [],
          hourly: clientEventSummary?.hourly ?? [],
        },
      });
    } catch (snapshotError) {
      console.error("[admin] ops-center snapshot failed", snapshotError);
      setError("Ops verileri alinamadi. Lutfen tekrar dene.");
      setSnapshot(EMPTY_SNAPSHOT);
    } finally {
      setLoading(false);
    }
  }, [analyticsAction, analyticsHours]);

  useEffect(() => {
    if (!allowed) {
      return;
    }
    void loadSnapshot();
  }, [allowed, loadSnapshot]);

  const topRisk = useMemo(() => {
    if (snapshot.security.creatorAccessViolations > 0) {
      return "Creator erisim ihlali var";
    }
    if (snapshot.security.logErrors > 0) {
      return "Son 24 saatte hata logu var";
    }
    if (snapshot.content.pendingSubmissions + snapshot.content.pendingParts > 0) {
      return "Onay kuyrugu bekliyor";
    }
    return "Kritik risk gorunmuyor";
  }, [snapshot]);

  const handleExportAnalytics = async () => {
    setExportingAnalytics(true);
    const blob = await exportClientEventsCsv(analyticsHours, analyticsAction);
    setExportingAnalytics(false);

    if (!blob) {
      await notifyDialog("Analytics export işlemi başarısız oldu.", "error");
      return;
    }

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `client-events-${new Date().toISOString()}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (permissionLoading) {
    return <div className="p-8 text-center text-sm text-gray-500">Yetkiler kontrol ediliyor...</div>;
  }

  if (!allowed) {
    return <div className="p-8 text-center text-sm text-red-500">Bu sayfaya erisim izniniz yok.</div>;
  }

  return (
    <div className="space-y-5">
      <header className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-xl font-semibold text-slate-900">Ops Merkezi</h1>
            <p className="text-sm text-slate-600">
              Odeme operasyonu, kullanici yonetimi, icerik kuyrugu ve risk sinyalleri tek ekranda.
            </p>
          </div>
          <button
            type="button"
            onClick={() => void loadSnapshot()}
            disabled={loading}
            className="rounded bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700 disabled:opacity-60"
          >
            {loading ? "Yukleniyor..." : "Yenile"}
          </button>
        </div>
        <div className="mt-3 flex flex-wrap gap-2">
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            Saat:
            <select
              value={analyticsHours}
              onChange={(event) => setAnalyticsHours(Number(event.target.value))}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800"
            >
              <option value={6}>6h</option>
              <option value={24}>24h</option>
              <option value={72}>72h</option>
              <option value={168}>168h</option>
            </select>
          </label>
          <label className="inline-flex items-center gap-2 text-xs text-slate-600">
            Action:
            <select
              value={analyticsAction}
              onChange={(event) => setAnalyticsAction(event.target.value)}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-800"
            >
              <option value="all">all</option>
              <option value="experiment_exposure">experiment_exposure</option>
              <option value="adsense_slot_push_attempt">adsense_slot_push_attempt</option>
              <option value="adsense_slot_push_success">adsense_slot_push_success</option>
              <option value="adsense_slot_push_failed">adsense_slot_push_failed</option>
            </select>
          </label>
          <button
            type="button"
            onClick={() => void handleExportAnalytics()}
            disabled={exportingAnalytics}
            className="rounded border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
          >
            {exportingAnalytics ? "CSV hazirlaniyor..." : "Analytics CSV indir"}
          </button>
        </div>
        <div className="mt-3 text-xs text-slate-500">Risk durumu: {topRisk}</div>
      </header>

      {error && <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        <Card title="Finance Ops">
          <Row label="Odeme bekleyen creator" value={snapshot.finance.outstandingCreators} />
          <Row label="Odeme token toplam" value={`${snapshot.finance.outstandingShareToken.toFixed(2)} ZTN`} />
          <Row label="Odeme tutar toplam" value={`${snapshot.finance.outstandingCurrencyAmount.toFixed(2)} TRY`} />
          <Row label="Acik payout kaydi" value={snapshot.finance.openPayoutRecords} />
          <Row label="Tamamlanmis payout" value={snapshot.finance.paidPayoutRecords} />
        </Card>

        <Card title="User 360">
          <Row label="Toplam kullanici" value={snapshot.users.totalUsers} />
          <Row label="Orneklenen kayit" value={snapshot.users.sampleSize} />
          <Row label="Platform ban" value={snapshot.users.bannedAllCount} />
          <Row label="Yorum ban" value={snapshot.users.commentBannedCount} />
          <Row label="Creator rolu" value={snapshot.users.creatorCount} />
          <Row label="Admin/Mod rolu" value={snapshot.users.staffCount} />
          <Row label="View metrigi disinda" value={snapshot.users.excludedFromViewMetricsCount} />
        </Card>

        <Card title="Content Ops">
          <Row label="Bekleyen seri" value={snapshot.content.pendingSubmissions} />
          <Row label="Bekleyen bolum" value={snapshot.content.pendingParts} />
        </Card>

        <Card title="Security Ops">
          <Row label="Son 24s hata logu" value={snapshot.security.logErrors} />
          <Row label="Creator erisim ihlali" value={snapshot.security.creatorAccessViolations} />
        </Card>

        <Card title="Analytics">
          <Row label="Toplam event" value={snapshot.analytics.totalEvents} />
          <Row label="AdSense denemesi" value={snapshot.analytics.adsenseAttempts} />
          <Row label="AdSense basari orani" value={`%${snapshot.analytics.adsenseSuccessRate.toFixed(2)}`} />
          <Row label="Top varyant" value={snapshot.analytics.topVariant} />
          <Row label="Top slot" value={snapshot.analytics.topSlot} />
          <Row label="Pencere" value={`${snapshot.analytics.selectedHours}h / ${snapshot.analytics.selectedAction}`} />
        </Card>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-800">Analytics Saatlik Trend</h2>
        <p className="mt-1 text-xs text-slate-500">
          Baslangic: {snapshot.analytics.sinceUtc ? new Date(snapshot.analytics.sinceUtc).toLocaleString("tr-TR") : "-"}
        </p>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="px-2 py-2 font-semibold">Saat</th>
                <th className="px-2 py-2 font-semibold">Toplam</th>
                <th className="px-2 py-2 font-semibold">Attempt</th>
                <th className="px-2 py-2 font-semibold">Success</th>
                <th className="px-2 py-2 font-semibold">Failed</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.analytics.hourly.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 text-slate-400">
                    Bu filtre icin saatlik event verisi bulunamadi.
                  </td>
                </tr>
              ) : (
                snapshot.analytics.hourly.slice(-24).map((item) => (
                  <tr key={item.hourUtc} className="border-b border-slate-100 text-slate-700">
                    <td className="px-2 py-2">{new Date(item.hourUtc).toLocaleString("tr-TR", { hour: "2-digit", day: "2-digit", month: "2-digit" })}</td>
                    <td className="px-2 py-2 font-semibold">{item.total}</td>
                    <td className="px-2 py-2">{item.attempts}</td>
                    <td className="px-2 py-2">{item.success}</td>
                    <td className="px-2 py-2">{item.failed}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-800">A/B Funnel (Varyant Bazlı)</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="min-w-full text-left text-xs">
            <thead>
              <tr className="border-b border-slate-200 text-slate-500">
                <th className="px-2 py-2 font-semibold">Varyant</th>
                <th className="px-2 py-2 font-semibold">Attempt</th>
                <th className="px-2 py-2 font-semibold">Success</th>
                <th className="px-2 py-2 font-semibold">Failed</th>
                <th className="px-2 py-2 font-semibold">Success Rate</th>
              </tr>
            </thead>
            <tbody>
              {snapshot.analytics.variantBreakdown.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-2 py-3 text-slate-400">
                    Varyant bazlı funnel verisi bulunamadı.
                  </td>
                </tr>
              ) : (
                snapshot.analytics.variantBreakdown.map((item) => (
                  <tr key={item.variant} className="border-b border-slate-100 text-slate-700">
                    <td className="px-2 py-2 font-semibold">{item.variant}</td>
                    <td className="px-2 py-2">{item.attempts}</td>
                    <td className="px-2 py-2">{item.success}</td>
                    <td className="px-2 py-2">{item.failed}</td>
                    <td className="px-2 py-2">%{item.successRate.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-800">Hizli Akislar</h2>
        <div className="mt-3 flex flex-wrap gap-2">
          <QuickLink href="/admin/revenue" label="Gelir Raporu" />
          <QuickLink href="/admin/royalties" label="Royalty Odemeleri" />
          <QuickLink href="/admin/users" label="Kullanici 360" />
          <QuickLink href="/admin/content" label="Icerik Moderasyonu" />
          <QuickLink href="/admin/logs" label="Log Merkezi" />
          <QuickLink href="/admin/notifications" label="Bildirim Merkezi" />
        </div>
      </section>
    </div>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
      <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
      <div className="mt-2 space-y-1.5">{children}</div>
    </article>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-800">{value}</span>
    </div>
  );
}

function QuickLink({ href, label }: { href: string; label: string }) {
  return (
    <a
      href={href}
      className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100"
    >
      {label}
    </a>
  );
}
